<template>
  <div
    class="relative flex gap-2 justify-between items-center"
    :class="{ 'opacity-50': disabled }"
  >
    <input
      :id="id"
      ref="inputRef"
      :value="modelValue"
      :disabled="disabled"
      :maxlength="length"
      :pattern="`[0-9]{${length}}`"
      :aria-label="ariaLabel"
      :aria-invalid="error || undefined"
      :name="name"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      class="absolute inset-0 w-full h-full opacity-0 cursor-text z-10 disabled:cursor-not-allowed"
      style="caret-color: transparent"
      @input="onInput"
      @focus="focused = true"
      @blur="focused = false"
    />
    <div
      v-for="index in length"
      :key="index"
      aria-hidden="true"
      class="flex-1 max-w-16 w-full h-10 px-2 py-3 border rounded-lg bg-surface text-base text-center shadow-lg flex items-center justify-center"
      :class="[
        error
          ? isCellActive(index - 1)
            ? 'border-basics-accent-red outline outline-4 outline-offset-2 outline-brand-quaternary-40'
            : 'border-basics-accent-red'
          : isCellActive(index - 1)
            ? 'border-border-pressed outline outline-4 outline-offset-2 outline-brand-quaternary-40'
            : 'border-border',
        modelValue[index - 1] ? 'text-text' : 'text-text-secondary',
      ]"
    >
      {{ modelValue[index - 1] || "○" }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";

const props = withDefaults(
  defineProps<{
    id?: string;
    name?: string;
    length?: number;
    disabled?: boolean;
    error?: boolean;
    ariaLabel?: string;
  }>(),
  { length: 6 },
);

const modelValue = defineModel<string>({ default: "" });

const emit = defineEmits<{
  complete: [value: string];
}>();

const inputRef = useTemplateRef<HTMLInputElement>("inputRef");
const focused = ref(false);

function onInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  const digits = target.value.replace(/\D/g, "").slice(0, props.length);
  if (digits !== target.value) {
    target.value = digits;
  }
  const prev = modelValue.value;
  if (digits === prev) return;
  modelValue.value = digits;
  if (digits.length === props.length && prev.length !== props.length) {
    emit("complete", digits);
  }
}

function isCellActive(index: number): boolean {
  if (!focused.value || props.disabled) return false;
  const filled = modelValue.value.length;
  if (filled >= props.length) return index === props.length - 1;
  return index === filled;
}

function focus(): void {
  inputRef.value?.focus();
}

defineExpose({ focus });
</script>
